<template>
  <div class="coupon-template-multi-selector">
    <div v-if="loading" class="loading-templates">
      <i class="ri-loader-4-line spin"></i>
      <span>載入優惠券模板中...</span>
    </div>
    <template v-else>
      <div class="template-selector">
        <div class="template-search">
          <input
            v-model="searchQuery"
            type="text"
            class="form-control"
            placeholder="搜尋優惠券模板..."
            :disabled="disabled"
            @focus="$event.target.select()"
          />
        </div>
        <div class="template-list">
          <div
            v-for="template in filteredTemplates"
            :key="template.id"
            class="template-item"
            :class="{ selected: isSelected(template.id) }"
            @click="!disabled && toggleTemplate(template.id)"
          >
            <input
              type="checkbox"
              :checked="isSelected(template.id)"
              :disabled="disabled"
              @change="toggleTemplate(template.id)"
              @click.stop
            />
            <div class="coupon-thumb-wrap">
              <img
                v-if="template.imageUrl"
                :src="template.imageUrl"
                :alt="template.templateName || template.name"
                class="coupon-thumb"
                loading="lazy"
              />
              <span v-else class="coupon-thumb-placeholder" title="未設定圖片">
                <i class="ri-coupon-line"></i>
              </span>
            </div>
            <div class="template-info">
              <div class="template-name">{{ template.templateName || template.name }}</div>
              <div class="template-details">
                <span class="template-type">{{ getCouponTypeText(template.couponType) }}</span>
                <span v-if="template.discountValue" class="template-discount">
                  {{ formatDiscount(template.discountValue, template.discountUnit) }}
                </span>
                <span v-if="template.validityDays" class="template-validity">
                  有效 {{ template.validityDays }} 天
                </span>
              </div>
            </div>
          </div>
          <div v-if="filteredTemplates.length === 0" class="no-templates">
            <i class="ri-inbox-line"></i>
            <p>沒有找到優惠券模板</p>
            <small v-if="searchQuery.trim()">請嘗試其他搜尋關鍵字</small>
          </div>
        </div>
    </div>
    <p v-if="exhaustedSelected.length" class="reward-stock-warning">
      {{ exhaustedSelected.map((template) => template.templateName || template.name).join('、') }} 已達總發行上限，發送時會失敗
    </p>
    <div v-if="selectedTemplates.length > 0" class="selected-templates">
        <div class="selected-label">已選取 {{ selectedTemplates.length }} 個優惠券模板：</div>
        <div class="selected-tags">
          <span
            v-for="template in selectedTemplates"
            :key="template.id"
            class="selected-tag"
          >
            <span class="selected-tag-thumb-wrap">
              <img
                v-if="template.imageUrl"
                :src="template.imageUrl"
                :alt="template.templateName || template.name"
                class="selected-tag-thumb"
                loading="lazy"
              />
              <span v-else class="selected-tag-thumb-placeholder">
                <i class="ri-coupon-line"></i>
              </span>
            </span>
            {{ template.templateName || template.name }}
            <button
              type="button"
              class="remove-tag"
              :disabled="disabled"
              @click="removeTemplate(template.id)"
            >
              <i class="ri-close-line"></i>
            </button>
          </span>
        </div>
      </div>
      <small v-if="hint" class="form-text">{{ hint }}</small>
    </template>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { isCouponTemplateExhausted } from '@/utils/rewardStockWarning.js';

export default {
  name: 'CouponTemplateMultiSelector',
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    templates: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: '可選擇多個優惠券模板'
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const searchQuery = ref('');

    const selectedIds = computed(() => props.modelValue || []);

    const filteredTemplates = computed(() => {
      const query = searchQuery.value.trim().toLowerCase();
      if (!query) return props.templates;
      return props.templates.filter((template) => {
        const name = (template.templateName || template.name || '').toLowerCase();
        const type = (template.couponType || '').toLowerCase();
        return name.includes(query) || type.includes(query);
      });
    });

    const selectedTemplates = computed(() =>
      props.templates.filter((t) => selectedIds.value.includes(t.id))
    );

    const exhaustedSelected = computed(() => selectedTemplates.value.filter(isCouponTemplateExhausted));

    const isSelected = (templateId) => selectedIds.value.includes(templateId);

    const toggleTemplate = (templateId) => {
      if (props.disabled) return;
      const next = [...selectedIds.value];
      const idx = next.indexOf(templateId);
      if (idx > -1) {
        next.splice(idx, 1);
      } else {
        next.push(templateId);
      }
      emit('update:modelValue', next);
    };

    const removeTemplate = (templateId) => {
      if (props.disabled) return;
      emit(
        'update:modelValue',
        selectedIds.value.filter((id) => id !== templateId)
      );
    };

    const getCouponTypeText = (type) => {
      const typeMap = {
        discount: '折扣券',
        fixed_amount: '折價券',
        free_shipping: '免運券',
        gift: '贈品券'
      };
      return typeMap[type] || type || '優惠券';
    };

    const formatDiscount = (value, unit) => {
      if (!value) return '';
      if (unit === 'percent') return `${value}% 折扣`;
      if (unit === 'fixed') return `折抵 NT$ ${value}`;
      return `${value} ${unit || ''}`.trim();
    };

    return {
      searchQuery,
      filteredTemplates,
      selectedTemplates,
      exhaustedSelected,
      isSelected,
      toggleTemplate,
      removeTemplate,
      getCouponTypeText,
      formatDiscount
    };
  }
};
</script>

<style scoped>
.loading-templates {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px;
  color: #666;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.template-selector {
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
}

.template-search {
  padding: 12px;
  border-bottom: 1px solid #eee;
}

.template-search input {
  width: 100%;
}

.template-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 8px;
}

.template-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.template-item:hover {
  background: #f5f5f5;
  border-color: #1976d2;
}

.template-item.selected {
  background: #e3f2fd;
  border-color: #1976d2;
}

.template-item input[type="checkbox"] {
  flex-shrink: 0;
  cursor: pointer;
}

.coupon-thumb-wrap {
  flex-shrink: 0;
}

.coupon-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  display: block;
}

.coupon-thumb-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  border: 1px dashed #d1d5db;
  background: #f9fafb;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.template-info {
  flex: 1;
}

.template-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.template-details {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #666;
}

.template-type {
  padding: 2px 8px;
  background: #f0f0f0;
  border-radius: 12px;
}

.template-discount {
  color: #1976d2;
  font-weight: 500;
}

.no-templates {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.no-templates i {
  font-size: 48px;
  margin-bottom: 12px;
  display: block;
}

.selected-templates {
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
}

.reward-stock-warning {
  color: #b45309;
  font-size: 13px;
  margin: 8px 0 0;
}

.selected-label {
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selected-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 4px;
  background: #1976d2;
  color: white;
  border-radius: 16px;
  font-size: 14px;
}

.selected-tag-thumb-wrap {
  flex-shrink: 0;
}

.selected-tag-thumb {
  width: 24px;
  height: 24px;
  object-fit: cover;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.4);
  display: block;
}

.selected-tag-thumb-placeholder {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.remove-tag {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  font-size: 16px;
  line-height: 1;
}

.remove-tag:hover:not(:disabled) {
  opacity: 0.8;
}

.remove-tag:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.form-text {
  display: block;
  margin-top: 8px;
  font-size: 0.875rem;
  color: #6b7280;
}
</style>
